import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/lib/api-functions/api-auth';
import {
  fetchTemplates,
  getCachedTemplates,
  clearGlossaryBlueprintsCache,
  templatesStore,
} from "@/lib/stores/templatesStore";
import { GlossaryBlueprint } from "@/types-games";

interface UseGlossaryBlueprintsDataOptions {
  gameId?: string; // Optional: if not provided, uses selected game
  forceReload?: boolean;
  autoLoad?: boolean;
}

interface UseGlossaryBlueprintsDataReturn {
  // Data
  glossaryBlueprints: GlossaryBlueprint[];
  getBlueprintDocuments: () => GlossaryBlueprint[];
  getBlueprintGlossaries: () => GlossaryBlueprint[];
  getBlueprintById: (id: string) => GlossaryBlueprint | undefined;
  // Loading states
  isLoading: boolean;

  // Actions
  loadData: (forceReload?: boolean) => Promise<void>;
  clearCache: () => Promise<void>;

  // Cached data access
  getCachedData: () => GlossaryBlueprint[];
}

export function useGlossaryBlueprintsData({
  gameId,
  forceReload = false,
  autoLoad = true,
}: UseGlossaryBlueprintsDataOptions = {}): UseGlossaryBlueprintsDataReturn {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const [glossaryBlueprints, setGlossaryBlueprints] = useState<GlossaryBlueprint[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load glossary blueprints data
  const loadData = async (forceReloadData = false) => {
    if (!gameId) {
      console.warn("No game ID available for loading glossary blueprints");
      return;
    }

    setIsLoading(true);
    try {
      // Fetch glossary blueprints from the API
      const fetchedBlueprints = await fetchTemplates(gameId, toast);
      setGlossaryBlueprints(fetchedBlueprints);
    } catch (error) {
      console.error("Error loading glossary blueprints:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear cache
  const clearCache = async () => {
    try {
      // Clear the store data
      await clearGlossaryBlueprintsCache();
      setGlossaryBlueprints([]);
      toast({
        title: "Cache cleared",
        description: "Glossary blueprints cache cleared",
      });
    } catch (error) {
      console.error("Error clearing cache:", error);
    }
  };

  // Get cached data without loading
  const getCachedData = (): GlossaryBlueprint[] => {
    if (!gameId) return [];
    return getCachedTemplates(gameId);
  };
  const getBlueprintDocuments=()=>{
    return getCachedTemplates(gameId).filter((blueprint)=>blueprint.type==="document");
  }
  
  const getBlueprintGlossaries=()=>{
    return getCachedTemplates(gameId).filter((blueprint)=>blueprint.type==="glossary");
  }
  // get blueprint by id
  const getBlueprintById=(id:string)=>{
    return getCachedTemplates(gameId).find((blueprint)=>blueprint.id===id);
  }

  // Auto-load on mount only if we have a game ID
  useEffect(() => {
    if (autoLoad && gameId) {
      loadData(forceReload);
    }
  }, [gameId, autoLoad, forceReload]);

  // Subscribe to store changes
  useEffect(() => {
    if (!gameId) return;

    // Get initial data from store
    const initialData = getCachedTemplates(gameId);
    setGlossaryBlueprints(initialData);

    // Subscribe to store changes
    const unsubscribe = templatesStore.subscribe(() => {
      const cachedBlueprints = getCachedTemplates(gameId);
      setGlossaryBlueprints(cachedBlueprints);
    });

    return () => unsubscribe();
  }, [gameId]);

  return {
    // Data
    glossaryBlueprints,
    getBlueprintDocuments,
    getBlueprintGlossaries,
    getBlueprintById,
    // Loading states
    isLoading,

    // Actions
    loadData,
    clearCache,

    // Cached data access
    getCachedData,
  };
} 